import { Component, computed, ElementRef, inject, input, output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ToolchainJobService } from '../../../services/api-services/toolchains/toolchain-job.service';
import { Toolchain, ToolchainJob } from '../../../utils/models/toolchain.models';
import { JobTimelineComponent } from './job-timeline.component';
import {
  getJobRunStatusPresentation,
  isFinishedJobStatus,
} from '../../../utils/job-run-presentation';
import { ToolchainTimelineLegendComponent } from './toolchain-timeline-legend/toolchain-timeline-legend.component';
import { ToolchainTimelineStep } from './toolchain-timeline.types';
import { ToolJobService } from '../../../services/api-services/tools/tool-job.service';
import { fillInputMapping, requiresPopulatedOutputs } from './toolchain-timeline.util';
import { scrollTimeline } from './job-timeline-scroll.util';

@Component({
  selector: 'app-toolchain-timeline',
  templateUrl: './toolchain-timeline.component.html',
  styleUrls: ['./toolchain-timeline.component.scss'],
  imports: [JobTimelineComponent, ToolchainTimelineLegendComponent],
})
export class ToolchainTimelineComponent {
  /** Inputs. */
  readonly toolchain = input<Toolchain>();
  readonly job = input<ToolchainJob>();
  readonly isInitialLoading = input(false);

  /** Outputs. */
  readonly jobUpdated = output();
  readonly jobStarting = output<boolean>();

  /** Services. */
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly router = inject(Router);
  private readonly toolchainJobService = inject(ToolchainJobService);
  private readonly toolJobService = inject(ToolJobService);

  /** State. */
  readonly populatedOutputs = this.toolJobService.getOutputs(() => {
    const mapping = this.activeMapping();
    return mapping &&
      requiresPopulatedOutputs(mapping.previousJob, mapping.tool, mapping.currentStep.inputMapping)
      ? mapping.previousJob.toolJobId
      : undefined;
  });

  /** Returns the rendered state for every toolchain step. */
  readonly timelineSteps = computed<ToolchainTimelineStep[]>(() => {
    const job = this.job();
    const toolchain = this.toolchain();
    if (!toolchain) return [];
    const toolJobs = job?.toolJobs ?? [];

    const activeIndex = toolchain.steps.findIndex(
      (_, index) => !isFinishedJobStatus(toolJobs[index]?.status)
    );

    return toolchain.steps.map((step, index) => ({
      step,
      job: toolJobs[index] ?? null,
      name: toolchain.tools[step.toolId]?.name ?? 'Tool',
      presentation: getJobRunStatusPresentation(toolJobs[index]?.status ?? 'Pending'),
      active: index === activeIndex && job?.status !== 'Error',
      future: activeIndex !== -1 && index > activeIndex,
      rerun: () => this.restoreStep(step.stepNo),
    }));
  });

  /** Returns the output mapping for the active pending step. */
  private readonly activeMapping = computed(() => {
    const steps = this.timelineSteps();
    const index = steps.findIndex(step => step.active && !step.job);
    if (index < 1) return undefined;

    const previousJob = steps[index - 1].job;
    const currentStep = steps[index].step;
    const tool = this.toolchain()?.tools[currentStep.toolId];
    return previousJob && tool ? { previousJob, currentStep, tool } : undefined;
  });

  /** Returns the input mapping populated with prior step outputs. */
  readonly activeInputMapping = computed(() => {
    const mapping = this.activeMapping();
    if (!mapping) return undefined;

    return fillInputMapping(
      mapping.previousJob,
      mapping.tool,
      mapping.currentStep.inputMapping,
      this.populatedOutputs.value()
    );
  });

  /** Constructor. */
  constructor() {
    scrollTimeline(() => {
      if (this.isInitialLoading() || this.populatedOutputs.isLoading()) return undefined;

      const steps = this.timelineSteps();
      const parameterTarget = this.element.querySelector<HTMLElement>('app-input-parameter-node');
      const parameterStep = parameterTarget?.closest<HTMLElement>('app-job-timeline')?.id;

      if (parameterTarget && parameterStep) {
        return { element: parameterTarget, key: `${parameterStep}:parameters` };
      }

      let index = steps.findIndex(step => step.active);
      if (index === -1) index = steps.findLastIndex(step => !!step.job);
      if (index === -1) return undefined;

      const item = steps[index];
      const element = this.element.querySelector<HTMLElement>(
        `#toolchain-step-${item.step.stepNo} app-tool-node`
      );
      if (!element) return undefined;

      return {
        element,
        key: `${item.step.stepNo}:tool:${item.job?.status ?? 'pending'}`,
      };
    });
  }

  /** Starts the toolchain job. */
  readonly startToolchainJob = async (form: FormGroup): Promise<void> => {
    const toolchain = this.toolchain();
    if (!toolchain) return;

    const result = await this.toolchainJobService.createJob(toolchain.toolchainId, form);
    await this.router.navigate([
      '/toolchains',
      toolchain.toolchainId,
      'results',
      result.toolchainJobId,
    ]);
  };

  /** Starts the next toolchain step. */
  readonly startNextTool = async (form: FormGroup): Promise<void> => {
    const job = this.job();
    if (!job) return;

    const result = await this.toolchainJobService.triggerNextStep(
      job.toolchain.toolchainId,
      job.toolchainJobId,
      form
    );
    if (result.toolJobId) this.jobUpdated.emit();
  };

  /** Returns the appropriate job-start handler for a timeline step. */
  startJobFor(
    item: ToolchainTimelineStep,
    isFirstStep: boolean
  ): ((form: FormGroup) => Promise<void>) | undefined {
    if (!this.job() && isFirstStep) return this.startToolchainJob;
    if (item.active && !item.job && !item.step.automaticStart) return this.startNextTool;
    return undefined;
  }

  /** Restores the toolchain job to a step. */
  private async restoreStep(stepNo: number): Promise<void> {
    const job = this.job();
    if (!job) return;

    await this.toolchainJobService.restoreStep(job.toolchainJobId, stepNo);
    this.jobUpdated.emit();
  }
}
